/* eslint-disable react-refresh/only-export-components */
import { client, MinimalEntityDto } from "@/client";
import { useEditableData } from "@/components/custom/editable-data";
import {
  FormInputItem,
  FormTextAreaItem,
} from "@/components/custom/form-inputs/debounced-input";
import { FormSelectInputItem } from "@/components/custom/form-inputs/select-input/select-input";
import { FormSelectInputAsyncPaginateQueryItem } from "@/components/custom/form-inputs/select-input/select-input-async-pagination";
import { cn, NIL } from "@/lib/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

export function usePrefix({ prefix, name }: { name: string; prefix?: string }) {
  return React.useMemo(
    () => [prefix, name].filter(Boolean).join("."),
    [prefix, name],
  );
}

export function Devices({
  prefix,
  name = "deviceIds",
  className,
  container,
  ...props
}: Omit<
  Parameters<typeof FormSelectInputAsyncPaginateQueryItem>[0],
  | "form"
  | "name"
  | "mapping"
  | "queryKey"
  | "queryCreator"
  | "createOptions"
  | "hasMore"
  | "requestVariable"
> & { prefix?: string; name?: string }) {
  const { form, editMode, onChange } = useEditableData<{
    deviceIds: number[];
  }>();
  const { t } = useTranslation();
  const perPage = 10;

  const deviceData = form.getValues("server" as any);
  const paramName = usePrefix({ name, prefix }) as "deviceIds";
  if (!editMode && deviceData) {
    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <div className="text-sm font-medium">{t("devices")}</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/dashboard/resources/devices/${deviceData.id}`}>
              {deviceData.name}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name={paramName}
      label={t("devices")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.devices.listMinimal.queryKey} // cache
      queryCreator={client.devices.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isDisabled={!editMode}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      className={cn("w-auto", className)}
      {...props}
      isNullable={onChange ? true : props.isNullable}
      empty={onChange ? NIL : undefined}
    />
  );
}

export function Clusters({
  prefix,
  name = "clusterIds",
  className,
  container,
  ...props
}: Omit<
  Parameters<typeof FormSelectInputAsyncPaginateQueryItem>[0],
  | "form"
  | "name"
  | "label"
  | "isMulti"
  | "mapping"
  | "queryKey"
  | "queryCreator"
  | "createOptions"
  | "hasMore"
  | "requestVariable"
> & { prefix?: string; name?: string }) {
  const { form, editMode, onChange } = useEditableData<{
    clusterIds: number[];
  }>();
  const { t } = useTranslation();
  const perPage = 10;

  const clustersData = form.getValues("clusters" as any);
  const paramName = usePrefix({ name, prefix }) as "clusterIds";
  if (!editMode && clustersData && clustersData.length > 0) {
    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <div className="text-sm font-medium">{t("clusters")}</div>
        <div className="flex flex-wrap gap-2">
          {clustersData.map((cluster) => (
            <Button key={cluster.id} variant="outline" size="sm" asChild>
              <Link to={`/dashboard/resources/clusters/${cluster.id}`}>
                {cluster.name}
              </Link>
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name={paramName}
      label={t("clusters")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.clusters.listMinimal.queryKey} // cache
      queryCreator={client.clusters.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isDisabled={!editMode}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      className={cn("w-auto", className)}
      {...props}
      isNullable={onChange ? true : props.isNullable}
      empty={onChange ? NIL : undefined}
    />
  );
}

export function Sites({
  prefix,
  name = "siteIds",
  className,
  container,
  ...props
}: Omit<
  Parameters<typeof FormSelectInputAsyncPaginateQueryItem>[0],
  | "form"
  | "name"
  | "label"
  | "isMulti"
  | "mapping"
  | "queryKey"
  | "queryCreator"
  | "createOptions"
  | "hasMore"
  | "requestVariable"
> & { prefix?: string; name?: string }) {
  const { form, editMode, onChange } = useEditableData<{ siteIds: number[] }>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name={usePrefix({ name, prefix }) as "siteIds"}
      label={t("sites")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.sites.listMinimal.queryKey} // cache
      queryCreator={client.sites.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isDisabled={!editMode}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      className={cn("w-auto", className)}
      {...props}
      isNullable={onChange ? true : props.isNullable}
      empty={onChange ? NIL : undefined}
    />
  );
}

export function Models({
  name = "modelIds",
  label,
  prefix,
  className,
  container,
  ...props
}: Omit<
  Parameters<typeof FormSelectInputAsyncPaginateQueryItem>[0],
  | "form"
  | "mapping"
  | "queryKey"
  | "queryCreator"
  | "createOptions"
  | "hasMore"
  | "requestVariable"
> & { prefix?: string; name?: string }) {
  const { form, editMode, onChange } = useEditableData<{
    modelIds: number[];
    inputModel?: MinimalEntityDto;
  }>();
  const { t } = useTranslation();
  const perPage = 10;

  const modelData = form.getValues("inputModel" as any);
  const paramName = usePrefix({ name, prefix }) as "modelIds";
  if (!editMode && modelData) {
    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <div className="text-sm font-medium">{label ?? t("models")}</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/dashboard/models/${modelData.id}`}>
              {modelData.name}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name={paramName}
      label={label ?? t("models")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.models.listMinimal.queryKey} // cache
      queryCreator={
        client.models.listMinimal.assign({ orderBy: "createdAt" })
          .asQueryOptions<any>
      }
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: [row.name, row.id ? ` (${row.id})` : undefined]
            .filter(Boolean)
            .join(" "),
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        search,
        limit: limit ?? perPage,
        page,
      })}
      isDisabled={!editMode}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      className={cn("w-auto", className)}
      isClearable={true}
      {...props}
      isNullable={onChange ? true : props.isNullable}
      empty={onChange ? NIL : undefined}
    />
  );
}

export function ClearableBoolean({
  prefix,
  name,
  label,
  className,
  container,
  ...props
}: Omit<
  Parameters<typeof FormSelectInputItem>[0],
  "form" | "isMulti" | "isClearable" | "mapping" | "options" | "isDisabled"
> & { prefix?: string }) {
  const { form, editMode, onChange } = useEditableData<{
    [key: string]: boolean | null | undefined;
  }>();
  const { t } = useTranslation();
  const mapping = (v: boolean | null | undefined) => ({
    value: v!,
    label: String(v),
  });
  return (
    <FormSelectInputItem
      form={form}
      name={usePrefix({ name, prefix })}
      label={label ?? t(name)}
      isMulti={false}
      isClearable={true}
      mapping={mapping}
      options={[false, true].map(mapping)}
      className={cn("w-auto", className)}
      isDisabled={!editMode}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      {...props}
      isNullable={onChange ? true : props.isNullable}
      empty={onChange ? NIL : undefined}
    />
  );
}

export function EditableInput({
  prefix,
  name,
  container,
  ...props
}: Omit<Parameters<typeof FormInputItem>[0], "form" | "disabled"> & {
  prefix?: string;
}) {
  const { form, editMode, onChange } = useEditableData<{
    [key: string]: string | undefined;
  }>();
  const { t } = useTranslation();
  return (
    <FormInputItem
      {...(props as any)}
      name={usePrefix({ name, prefix })}
      label={props?.label ?? t(name)}
      form={form}
      disabled={!editMode}
      fit={true}
      container={{
        ...container,
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      isUndefinable={onChange ? false : props.isUndefinable}
      isNullable={onChange ? true : props.isNullable}
    />
  );
}

export function EditableTextArea({
  prefix,
  name,
  container,
  copyWrapProps,
  ...props
}: Omit<Parameters<typeof FormTextAreaItem>[0], "form" | "disabled"> & {
  prefix?: string;
}) {
  const { form, editMode, onChange } = useEditableData<{
    [key: string]: string | undefined;
  }>();
  const { t } = useTranslation();
  return (
    <FormTextAreaItem
      rows={"auto"}
      {...(props as any)}
      name={usePrefix({ name, prefix })}
      label={props?.label ?? t(name)}
      form={form}
      disabled={!editMode}
      className={cn("w-full h-auto", props?.className)}
      container={{
        ...container,
        className: cn("w-full", container?.className),
        clear: container?.clear ?? (editMode ? undefined : null),
      }}
      copyWrapProps={{
        ...copyWrapProps,
        className: cn("w-full", copyWrapProps?.className),
      }}
      isUndefinable={onChange ? false : props.isUndefinable}
      isNullable={onChange ? true : props.isNullable}
    />
  );
}
